import { DatePicker, Input, Select } from "antd";
import { getInitials } from "helpers/initials";
import moment, { Moment } from "moment";
import React from "react";

export const PAGE_SIZE = 20;

export type DateRange = [Moment, Moment];

// Mặc định xem 7 ngày gần nhất.
export function getDefaultRange(): DateRange {
  return [moment().subtract(7, "days").startOf("day"), moment().endOf("day")];
}

export function isDefaultRange(range: DateRange) {
  const [from, to] = getDefaultRange();
  return range[0].isSame(from, "day") && range[1].isSame(to, "day");
}

export function formatDateTime(value: string) {
  return moment(value).format("DD/MM HH:mm");
}


export function Initials({ name, size }: { name: string; size?: "lg" }) {
  return (
    <span className={`system-log__initials${size === "lg" ? " system-log__initials--lg" : ""}`}>
      {getInitials(name)}
    </span>
  );
}

export function SearchField(props: {
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const { placeholder, value, onChange } = props;
  return (
    <Input
      className="system-log__search"
      allowClear
      prefix={<i className="tio-search" />}
      placeholder={placeholder}
      value={value}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

export function SelectField<T extends string | number>(props: {
  placeholder: string;
  value?: T;
  options: { value: T; label: string }[];
  onChange: (value?: T) => void;
  className?: string;
}) {
  const { placeholder, value, options, onChange, className } = props;
  return (
    <Select
      className={`system-log__select ${className ?? ""}`}
      allowClear
      placeholder={placeholder}
      value={value}
      options={options}
      onChange={(next) => onChange(next as T | undefined)}
    />
  );
}

export function RangeField(props: {
  value: DateRange;
  onChange: (value: DateRange) => void;
}) {
  const { value, onChange } = props;
  return (
    <DatePicker.RangePicker
      className="system-log__range"
      format="DD/MM/YYYY"
      allowClear={false}
      value={value}
      onChange={(next) => next && onChange(next as DateRange)}
    />
  );
}

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100].map((value) => ({ value, label: `${value}` }));

// Luôn đủ 7 ô (tính cả "…") khi nhiều trang, để nav không co giãn lúc chuyển trang.
function getPageWindow(current: number, pageCount: number): (number | "gap")[] {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, index) => index + 1);
  if (current <= 4) return [1, 2, 3, 4, 5, "gap", pageCount];
  if (current >= pageCount - 3) {
    return [1, "gap", pageCount - 4, pageCount - 3, pageCount - 2, pageCount - 1, pageCount];
  }
  return [1, "gap", current - 1, current, current + 1, "gap", pageCount];
}

// Chân bảng: số đếm bên trái, "Mỗi trang" và nav bên phải. Một trang thì chỉ còn số đếm.
export function LogPagination(props: {
  skip: number;
  take: number;
  total: number;
  unit: string;
  onChange: (skip: number, take: number) => void;
}) {
  const { skip, take, total, unit, onChange } = props;
  if (total === 0) return null;

  const pageCount = Math.ceil(total / take);
  const current = Math.floor(skip / take) + 1;
  const goTo = (page: number) => onChange((page - 1) * take, take);
  const format = (value: number) => value.toLocaleString("vi-VN");

  return (
    <div className="system-log__pager">
      <p className="system-log__pager-info tabular-nums">
        {pageCount > 1 && (
          <span className="system-log__pager-range">
            {format(skip + 1)} tới {format(Math.min(skip + take, total))} trong{" "}
          </span>
        )}
        {format(total)} {unit}
      </p>
      {total > PAGE_SIZE_OPTIONS[0].value && (
        <div className="system-log__pager-controls">
          <label className="system-log__pager-size">
            <span>Mỗi trang</span>
            <Select
              size="small"
              value={take}
              options={PAGE_SIZE_OPTIONS}
              onChange={(value) => onChange(0, value)}
            />
          </label>
          {pageCount > 1 && (
            <nav className="system-log__pager-nav" aria-label="Phân trang">
              <button
                type="button"
                aria-label="Trang trước"
                disabled={current === 1}
                onClick={() => goTo(current - 1)}
              >
                <i className="tio-chevron_left" />
              </button>
              {getPageWindow(current, pageCount).map((page, index) =>
                page === "gap" ? (
                  <span key={`gap-${index}`} className="system-log__pager-gap">
                    …
                  </span>
                ) : (
                  <button
                    type="button"
                    key={page}
                    aria-current={page === current ? "page" : undefined}
                    onClick={() => goTo(page)}
                  >
                    {page}
                  </button>
                )
              )}
              <span className="system-log__pager-compact tabular-nums">
                {current} / {pageCount}
              </span>
              <button
                type="button"
                aria-label="Trang sau"
                disabled={current === pageCount}
                onClick={() => goTo(current + 1)}
              >
                <i className="tio-chevron_right" />
              </button>
            </nav>
          )}
        </div>
      )}
    </div>
  );
}

// Nút "Xoá lọc" cuối hàng lọc, chỉ hiện khi có bộ lọc khác mặc định.
export function ResetFilterButton({ visible, onClick }: { visible: boolean; onClick: () => void }) {
  if (!visible) return null;
  return (
    <button type="button" className="system-log__reset" onClick={onClick}>
      Xoá lọc
    </button>
  );
}

export function LogEmpty({ title, onReset }: { title: string; onReset?: () => void }) {
  return (
    <div className="system-log__empty">
      <div className="system-log__empty-title">{title}</div>
      <div>Thử đổi khoảng ngày hoặc bỏ bớt bộ lọc.</div>
      {onReset && (
        <button type="button" className="system-log__btn" onClick={onReset}>
          Xoá bộ lọc
        </button>
      )}
    </div>
  );
}
